package com.iregained.site.components.sections.news.styles

import com.iregained.site.components.sections.home.styles.PlayRippleKeyframes
import com.iregained.site.theme.toSitePalette
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.CheckedState.Companion.from
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.hover
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.JustifyContent

val NewsGridContainerStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(colorMode.toSitePalette().custom.lightGray)
        .padding(topBottom = 80.px)
        .overflow { x(Overflow.Hidden) }
}

// --- FILTER BAR STYLES ---
val FilterBarWrapperStyle = CssStyle.base {
    Modifier.fillMaxWidth().maxWidth(1200.px).padding(leftRight = 24.px, bottom = 48.px).display(DisplayStyle.Flex)
        .flexWrap(FlexWrap.Wrap).justifyContent(JustifyContent.Center).gap(16.px)
}

val FilterButtonStyle = CssStyle {
    base {
        Modifier.padding(topBottom = 10.px, leftRight = 24.px).borderRadius(50.px) // Pill shape
            .border(1.px, LineStyle.Solid, Colors.LightGray).backgroundColor(Colors.White).color(Colors.DarkSlateGray)
            .fontSize(1.cssRem).fontWeight(FontWeight.SemiBold).cursor(Cursor.Pointer)
            .transition(Transition.of("all", 200.ms))
    }
    hover {
        Modifier.border(1.px, LineStyle.Solid, colorMode.toSitePalette().brand.primary)
            .color(colorMode.toSitePalette().brand.primary)
    }
}

val FilterButtonActiveStyle = CssStyle {
    base {
        Modifier.backgroundColor(colorMode.toSitePalette().brand.primary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().brand.primary)
            .color(Colors.White)
    }
    hover {
        // Overrides the base button's hover effect to ensure the text stays visible
        Modifier.color(Colors.White)
    }
}

// --- MASONRY GRID STYLES ---
val NewsCardGridStyle = CssStyle.base {
    Modifier.fillMaxWidth().maxWidth(1200.px).padding(leftRight = 24.px).gap(32.px)
}

val NewsCardStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().height(100.percent) // Ensures all cards in a row stretch
            .backgroundColor(Colors.White).borderRadius(16.px).border(1.px, LineStyle.Solid, Colors.LightGray)
            .overflow(Overflow.Hidden).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column)
            .transition(Transition.of("transform", 250.ms), Transition.of("box-shadow", 250.ms))
    }
    hover {
        Modifier.transform { translateY((-6).px) }.boxShadow(0.px, 16.px, 32.px, color = Colors.Black.copy(alpha = 8))
    }
}

val NewsCardImageWrapperStyle = CssStyle.base {
    Modifier.fillMaxWidth().position(Position.Relative)
}

val NewsCardImageStyle = CssStyle.base {
    Modifier.fillMaxSize().objectFit(ObjectFit.Cover)
}

// The dark overlay and play icon for video cards
val PlayRippleKeyframes = CssStyle {
    val palette = colorMode.toSitePalette()
    base {
        Modifier.zIndex(1).backgroundColor(palette.brand.primary) // Keep it permanently Teal to fix the vanish bug
            .animation(
                PlayRippleKeyframes.toAnimation(
                    // DECREASE DURATION HERE: Swapped 1.s for 600.ms to make the ripple pulse faster
                    duration = 500.ms,
                    iterationCount = AnimationIterationCount.Infinite // Pulses forever
                )
            ).transition(
                Transition.of("transform", 0.3.s), Transition.of("background-color", 0.3.s)
            )
    }
    hover {
        Modifier.scale(1.15) // Extra pop when hovered
            .backgroundColor(palette.brand.primaryHover) // Slightly darken on hover
    }
}

// The dark overlay and play icon for video cards
val VideoOverlayStyle = CssStyle.base {
    Modifier.position(Position.Absolute).top(0.px).left(0.px).right(0.px).bottom(0.px)
        .backgroundColor(Colors.Black.copy(alpha = 40)).display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
        .alignItems(AlignItems.Center)
}

val PlayIconStyle = CssStyle.base {
    Modifier.size(48.px).borderRadius(50.percent).backgroundColor(colorMode.toSitePalette().brand.primary)
        .display(DisplayStyle.Flex).justifyContent(JustifyContent.Center).alignItems(AlignItems.Center)
        .color(Colors.White)
        // Applies an infinite, smooth ripple pulse outward from the button
        .animation(
            PlayRippleKeyframes.toAnimation(
                duration = 1500.ms,
                timingFunction = AnimationTimingFunction.EaseOut,
                iterationCount = AnimationIterationCount.Infinite
            )
        )
}

val NewsCardContentStyle = CssStyle.base {
    Modifier.fillMaxWidth().padding(24.px).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column)
        .flexGrow(1) // Pushes the footer to the bottom
}

val NewsCardHeadlineStyle = CssStyle.base {
    Modifier.color(Colors.DarkSlateGray).fontSize(1.25.cssRem).fontWeight(FontWeight.Bold).lineHeight(1.4)
        .margin(bottom = 16.px)
}

val NewsCardFooterStyle = CssStyle.base {
    Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.SpaceBetween)
        .alignItems(AlignItems.Center).margin(top = 32.px) // Forces footer to bottom of flex container
}

val NewsCardSourceStyle = CssStyle.base {
    Modifier.color(Colors.Gray).fontSize(0.9.cssRem).fontWeight(FontWeight.SemiBold)
}

val NewsCardDateStyle = CssStyle.base {
    Modifier.color(Colors.Gray).fontSize(0.85.cssRem)
}